import receiptSettingsClient from 'rpc/receiptSettingsClient';
import {
  RetrieveReceiptSettingsRequest,
  RetrieveReceiptSettingsResponse,
  UpdateAutoReceiptSettingRequest,
  UpdateAutoReceiptSettingResponse,
  AutoReceiptSetting,
} from 'rpc/model/squareup/buyerportal/receiptsettings/service';
import { RequestStatus } from 'rpc/model/squareup/customers/request';
import {
  ReceiptSettingsData,
  ReceiptSettingsConverter,
} from 'routes/profile/models/ReceiptSettings';
import { callApi, getResponseError } from '../utils';
import { buyerportalApi } from '..';

export const receiptSettingsApi = buyerportalApi.injectEndpoints({
  endpoints: (builder) => ({
    retrieveReceiptSettings: builder.query<ReceiptSettingsData, void>({
      providesTags: ['ReceiptSettings'],
      async queryFn() {
        const response = await callApi<RetrieveReceiptSettingsResponse>(() =>
          receiptSettingsClient.retrieveReceiptSettings(
            RetrieveReceiptSettingsRequest.create({})
          )
        );

        const data = response.data;
        if (!data || data.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(data);
        }

        return {
          data: {
            cardGroups: (data.cardGroups ?? []).map(
              ReceiptSettingsConverter.fromCardGroup
            ),
            preferences: (data.preferences ?? []).map(
              ReceiptSettingsConverter.fromCardReceiptPreferences
            ),
          },
        };
      },
    }),
    updateAutoReceiptSetting: builder.mutation<
      null,
      {
        cardId: string;
        merchantId?: string;
        panSequenceNumber?: string;
        setting: AutoReceiptSetting;
      }
    >({
      invalidatesTags: ['ReceiptSettings'],
      onQueryStarted(
        { cardId, panSequenceNumber, setting, merchantId },
        { dispatch, queryFulfilled }
      ) {
        const updateResult = dispatch(
          receiptSettingsApi.util.updateQueryData(
            'retrieveReceiptSettings',
            undefined,
            (draft) => {
              const pref = draft.preferences.find(
                (p) =>
                  p.id === cardId &&
                  (panSequenceNumber === undefined ||
                    p.panSequenceNumber === panSequenceNumber)
              );
              if (!pref) {
                return;
              }

              if (merchantId) {
                const override = pref.merchantOverrides.find(
                  (o) => o.merchantId === merchantId
                );
                if (override) {
                  override.setting = setting;
                }
              } else {
                pref.autoReceiptSetting = setting;
              }
            }
          )
        );

        queryFulfilled.catch(updateResult.undo);
      },
      async queryFn({ cardId, panSequenceNumber, setting, merchantId }) {
        const response = await callApi<UpdateAutoReceiptSettingResponse>(() =>
          receiptSettingsClient.updateAutoReceiptSetting(
            UpdateAutoReceiptSettingRequest.create({
              cardId,
              merchantId,
              panSequenceNumber,
              setting,
            })
          )
        );

        const data = response.data;
        if (!data || data.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(data);
        }

        return { data: null };
      },
    }),
  }),
});

export const {
  useRetrieveReceiptSettingsQuery,
  useUpdateAutoReceiptSettingMutation,
} = receiptSettingsApi;
